import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./lib/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ComingSoonGate from "./components/ComingSoonGate";
import ChunkErrorBoundary, { lazyRoute } from "./components/ChunkErrorBoundary";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";
import "./App.css";

// Everything past the public entry pages loads on demand, so a landing-page
// visitor doesn't download Dashboard, AnalysisResult, ResumeProfile, etc.
const Dashboard = lazyRoute(() => import("./pages/Dashboard"));
const Analyze = lazyRoute(() => import("./pages/Analyze"));
const AnalysisResult = lazyRoute(() => import("./pages/AnalysisResult"));
const AtsScore = lazyRoute(() => import("./pages/AtsScore"));
const Watchlist = lazyRoute(() => import("./pages/Watchlist"));
const SkillsLedger = lazyRoute(() => import("./pages/SkillsLedger"));
const Transition = lazyRoute(() => import("./pages/Transition"));
const Billing = lazyRoute(() => import("./pages/Billing"));
const Settings = lazyRoute(() => import("./pages/Settings"));
const ResumeProfile = lazyRoute(() => import("./pages/ResumeProfile"));
const ProfilePage = lazyRoute(() => import("./pages/ProfilePage"));
const ExtensionConnect = lazyRoute(() => import("./pages/ExtensionConnect"));
const PrivacyPolicy = lazyRoute(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyRoute(() => import("./pages/TermsOfService"));

const RouteFallback = () => (
  <div className="min-h-screen bg-canvas" aria-busy="true" data-testid="route-fallback" />
);

function AppRouter() {
  const location = useLocation();
  if (location.pathname === "/auth/callback" && location.hash?.includes("token=")) {
    return <AuthCallback />;
  }
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/extension/connect"
        element={
          // requireOnboarded={false}: connecting the extension shouldn't be
          // blocked on finishing the resume upload; the analysis itself asks
          // for a resume when it needs one.
          <ProtectedRoute requireOnboarded={false}>
            <ExtensionConnect />
          </ProtectedRoute>
        }
      />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route
        path="/dashboard"
        element={
          // requireOnboarded={false}: a user who skips ResumeUploadModal
          // ("I'll do this later") lands here directly and sees the
          // dashboard's own persistent "upload your resume" banner instead
          // of being bounced to /profile/resume.
          <ProtectedRoute requireOnboarded={false}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analyze"
        element={
          <ProtectedRoute>
            <Analyze />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analysis/:id"
        element={
          <ProtectedRoute>
            <AnalysisResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ats"
        element={
          <ProtectedRoute>
            <AtsScore />
          </ProtectedRoute>
        }
      />
      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            <Watchlist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/skills-ledger"
        element={
          <ProtectedRoute>
            <SkillsLedger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transition"
        element={
          // Feature is built but not launched yet — the page and its
          // backend endpoint stay fully intact, just blurred + inert
          // behind a "coming soon" overlay. See ComingSoonGate.jsx.
          <ProtectedRoute>
            <ComingSoonGate message="Transition planning is coming soon. We're refining this feature based on early feedback.">
              <Transition />
            </ComingSoonGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          // requireOnboarded={false}: someone who skipped the resume upload
          // must still be able to sign out and delete their account here.
          <ProtectedRoute requireOnboarded={false}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute requireOnboarded={false}>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/resume"
        element={
          <ProtectedRoute requireOnboarded={false}>
            <ResumeProfile />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
